import Phaser from 'phaser';
import { frameKey, heroFrameSet, preloadFrameSet } from './assets';
import { applyMuteFromSave } from './audio';
import { drawNycSkyBackground, drawNycSilhouette } from './art/nycSilhouette';
import { HEROES, HeroId } from './heroes';
import { isPowerRangersUnlocked, loadSave, resetSave, setMuted } from './save';
import { getCurrentMode, setCurrentMode } from './mode';

const MENU_HEROES_MARVEL: HeroId[] = [
  'captain-america',
  'iron-man',
  'thor',
  'hulk',
  'scarlet-witch',
];

const MENU_HEROES_POWERRANGERS: HeroId[] = ['red-ranger', 'green-ranger', 'pink-ranger'];

export class MenuScene extends Phaser.Scene {
  private bgGraphics!: Phaser.GameObjects.Graphics;
  private silhouetteGraphics!: Phaser.GameObjects.Graphics;

  constructor() {
    super('Menu');
  }

  preload() {
    for (const id of [...MENU_HEROES_MARVEL, ...MENU_HEROES_POWERRANGERS]) {
      const set = heroFrameSet(id, 'idle');
      if (set.urls.length > 0) {
        preloadFrameSet(this, set);
      }
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');
    this.bgGraphics = this.add.graphics();
    this.silhouetteGraphics = this.add.graphics();
    applyMuteFromSave();
    this.layout();
    this.scale.on('resize', () => {
      this.children.removeAll(true);
      this.bgGraphics = this.add.graphics();
      this.silhouetteGraphics = this.add.graphics();
      this.layout();
    });
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    const mode = getCurrentMode();
    if (mode === 'powerrangers') {
      this.drawMorphinGrid(w, h);
    } else {
      drawNycSkyBackground(this.bgGraphics, w, h);
      drawNycSilhouette(this.silhouetteGraphics, w, h);
    }

    this.drawTitle(w, h);
    this.drawHeroRow(w, h);
    this.drawPlayButton(w, h);
    this.drawSecondaryButtons(w, h);
    this.drawCornerButtons(w, h);
    this.drawModeSwitch(w, h);
  }

  private drawModeSwitch(w: number, _h: number) {
    if (!isPowerRangersUnlocked()) return;

    const mode = getCurrentMode();
    const isPr = mode === 'powerrangers';
    const label = isPr ? '\u25c0 Avengers' : 'Power Rangers \u25b6';
    const color = isPr ? 0xffd24a : 0x22c55e;
    const cx = Math.floor(w / 2);
    const cy = 80;

    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(3, color, 1);
    g.fillRoundedRect(-110, -22, 220, 44, 14);
    g.strokeRoundedRect(-110, -22, 220, 44, 14);
    container.add(g);

    const txt = this.add
      .text(0, 0, label, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '18px',
        color: isPr ? '#ffd24a' : '#22c55e',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(txt);

    const hit = this.add.zone(0, 0, 240, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      setCurrentMode(isPr ? 'marvel' : 'powerrangers');
      this.scene.restart();
    });
    container.add(hit);

    this.tweens.add({
      targets: container,
      scale: 1.05,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawMorphinGrid(w: number, h: number) {
    this.bgGraphics.clear();
    this.silhouetteGraphics.clear();
    this.bgGraphics.fillStyle(0x060914, 1);
    this.bgGraphics.fillRect(0, 0, w, h);

    const gridCell = 40;
    this.bgGraphics.lineStyle(1, 0x22c55e, 0.25);
    for (let x = 0; x < w; x += gridCell) {
      this.bgGraphics.beginPath();
      this.bgGraphics.moveTo(x, 0);
      this.bgGraphics.lineTo(x, h);
      this.bgGraphics.strokePath();
    }
    for (let y = 0; y < h; y += gridCell) {
      this.bgGraphics.beginPath();
      this.bgGraphics.moveTo(0, y);
      this.bgGraphics.lineTo(w, y);
      this.bgGraphics.strokePath();
    }

    let seed = 4711;
    const rand = () => {
      seed = (seed + 0x6d2b79f5) >>> 0;
      let t = seed;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const rangerColors = [0xef4444, 0x22c55e, 0xec4899, 0x3b82f6, 0xfacc15, 0x0a0f1f];
    for (let i = 0; i < 50; i++) {
      const sx = rand() * w;
      const sy = rand() * h;
      const color = rangerColors[Math.floor(rand() * rangerColors.length)];
      this.bgGraphics.fillStyle(color, 0.7);
      this.bgGraphics.fillCircle(sx, sy, 2 + rand() * 2);
    }
  }

  private drawTitle(w: number, h: number) {
    const mode = getCurrentMode();
    const top = mode === 'powerrangers' ? 'POWER RANGERS' : 'AVENGERS';
    const bot = 'MATCH';
    const fill = mode === 'powerrangers' ? '#22c55e' : '#fbbf24';
    const stroke = '#7f1d1d';
    const cx = Math.floor(w / 2);
    const titleY = Math.floor(h * 0.16);

    const shadow = this.add
      .text(cx + 6, titleY + 6, top, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: Math.floor(Math.min(w * 0.12, 84)) + 'px',
        color: stroke,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const title = this.add
      .text(cx, titleY, top, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: Math.floor(Math.min(w * 0.12, 84)) + 'px',
        color: fill,
        fontStyle: 'bold',
        stroke,
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    const matchY = titleY + Math.floor(Math.min(w * 0.1, 72));
    const matchShadow = this.add
      .text(cx + 5, matchY + 5, bot, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: Math.floor(Math.min(w * 0.1, 68)) + 'px',
        color: stroke,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const match = this.add
      .text(cx, matchY, bot, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: Math.floor(Math.min(w * 0.1, 68)) + 'px',
        color: fill,
        fontStyle: 'bold',
        stroke,
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: [title, shadow, match, matchShadow],
      scale: 1.04,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawHeroRow(w: number, h: number) {
    const cy = Math.floor(h * 0.52);
    const mode = getCurrentMode();
    const menuHeroes = mode === 'powerrangers' ? MENU_HEROES_POWERRANGERS : MENU_HEROES_MARVEL;
    const count = menuHeroes.length;
    const maxSlot = 110;
    const slotW = Math.min(maxSlot, Math.floor((w * 0.85) / count));
    const totalW = slotW * count;
    const startX = Math.floor((w - totalW) / 2 + slotW / 2);

    menuHeroes.forEach((id, i) => {
      const x = startX + i * slotW;
      const hero = HEROES[id];
      const set = hero.idleFrames();
      if (set.urls.length > 0 && this.textures.exists(frameKey(set.prefix, 0))) {
        const sprite = this.add.sprite(x, cy, frameKey(set.prefix, 0));
        const scale = Math.min(slotW * 0.8, 72) / 92;
        sprite.setScale(scale);
        sprite.setOrigin(0.5, 0.5);
        this.tweens.add({
          targets: sprite,
          y: cy - 10,
          duration: 700 + i * 80,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    });
  }

  private drawPlayButton(w: number, h: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.72);

    const container = this.add.container(cx, cy);

    const g = this.add.graphics();
    g.fillStyle(0x22c55e, 1);
    g.lineStyle(5, 0x0a0f1f, 1);
    g.fillRoundedRect(-120, -40, 240, 80, 24);
    g.strokeRoundedRect(-120, -40, 240, 80, 24);

    g.fillStyle(0x0a0f1f, 1);
    g.beginPath();
    g.moveTo(-38, -22);
    g.lineTo(38, 0);
    g.lineTo(-38, 22);
    g.closePath();
    g.fillPath();

    const label = this.add
      .text(38, 0, 'PLAY', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '34px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    container.add(g);
    container.add(label);

    const hit = this.add
      .zone(0, 0, 280, 100)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('LevelMap'));
    container.add(hit);

    this.tweens.add({
      targets: container,
      scale: 1.07,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawSecondaryButtons(w: number, h: number) {
    const cy = Math.floor(h * 0.86);
    const cx = Math.floor(w / 2);
    const gap = 24;
    const btnW = 130;
    const btnH = 46;

    this.addPillButton(cx - btnW / 2 - gap / 2 - btnW / 2, cy, btnW, btnH, 'Heroes', 0xfbbf24, () =>
      this.scene.start('Roster'),
    );
    this.addPillButton(cx + btnW / 2 + gap / 2 - btnW / 2, cy, btnW, btnH, 'Power-Ups', 0xa855f7, () =>
      this.scene.start('PowerUps'),
    );
  }

  private addPillButton(
    cx: number,
    cy: number,
    btnW: number,
    btnH: number,
    label: string,
    color: number,
    onTap: () => void,
  ) {
    const container = this.add.container(cx + btnW / 2, cy);
    const g = this.add.graphics();
    g.fillStyle(color, 1);
    g.lineStyle(3, 0x0a0f1f, 1);
    g.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 18);
    g.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 18);
    container.add(g);

    const txt = this.add
      .text(0, 0, label, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '18px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(txt);

    const hit = this.add
      .zone(0, 0, btnW + 10, btnH + 10)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerup', onTap);
    container.add(hit);
  }

  private drawCornerButtons(w: number, h: number) {
    this.drawMuteButton(w, h);
    this.drawResetButton(w, h);
  }

  private drawMuteButton(w: number, _h: number) {
    const cx = w - 36;
    const cy = 36;
    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    this.paintMuteIcon(g);
    container.add(g);
    const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      const save = loadSave();
      setMuted(!save.audioMuted);
      applyMuteFromSave();
      g.clear();
      this.paintMuteIcon(g);
    });
    container.add(hit);
  }

  private paintMuteIcon(g: Phaser.GameObjects.Graphics) {
    const muted = loadSave().audioMuted;
    g.fillStyle(0x121a32, 1);
    g.lineStyle(2, 0xffd24a, 1);
    g.fillCircle(0, 0, 22);
    g.strokeCircle(0, 0, 22);

    g.fillStyle(0xffd24a, 1);
    g.beginPath();
    g.moveTo(-10, -4);
    g.lineTo(-2, -4);
    g.lineTo(4, -10);
    g.lineTo(4, 10);
    g.lineTo(-2, 4);
    g.lineTo(-10, 4);
    g.closePath();
    g.fillPath();

    if (muted) {
      g.lineStyle(3, 0xef4444, 1);
      g.beginPath();
      g.moveTo(-14, -14);
      g.lineTo(14, 14);
      g.strokePath();
    } else {
      g.lineStyle(2, 0xffd24a, 1);
      g.beginPath();
      g.arc(8, 0, 6, -Math.PI * 0.35, Math.PI * 0.35, false);
      g.strokePath();
      g.beginPath();
      g.arc(8, 0, 10, -Math.PI * 0.4, Math.PI * 0.4, false);
      g.strokePath();
    }
  }

  private drawResetButton(_w: number, h: number) {
    const cx = 36;
    const cy = h - 36;
    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    g.fillStyle(0x121a32, 1);
    g.lineStyle(2, 0xef4444, 1);
    g.fillCircle(0, 0, 22);
    g.strokeCircle(0, 0, 22);
    g.lineStyle(3, 0xef4444, 1);
    g.beginPath();
    g.arc(0, 0, 9, -Math.PI * 0.8, Math.PI * 0.8, false);
    g.strokePath();
    g.fillStyle(0xef4444, 1);
    g.beginPath();
    g.moveTo(6, -9);
    g.lineTo(12, -9);
    g.lineTo(9, -3);
    g.closePath();
    g.fillPath();
    container.add(g);

    const hit = this.add.zone(0, 0, 60, 60).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.showResetConfirm());
    container.add(hit);
  }

  private showResetConfirm() {
    const w = this.scale.width;
    const h = this.scale.height;
    const overlay = this.add.container(0, 0);

    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.7);
    dim.fillRect(0, 0, w, h);
    overlay.add(dim);
    const dimZone = this.add.zone(0, 0, w, h).setOrigin(0).setInteractive();
    overlay.add(dimZone);

    const cardW = Math.min(400, Math.floor(w * 0.8));
    const cardH = 220;
    const cardX = Math.floor((w - cardW) / 2);
    const cardY = Math.floor((h - cardH) / 2);
    const card = this.add.graphics();
    card.fillStyle(0x121a32, 1);
    card.lineStyle(3, 0xef4444, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 18);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 18);
    overlay.add(card);

    const title = this.add
      .text(w / 2, cardY + 44, 'Start over?', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '28px',
        color: '#ef4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    overlay.add(title);

    const body = this.add
      .text(w / 2, cardY + 90, 'This erases all your heroes,\nstones, stars and vibranium.', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '16px',
        color: '#e5e7eb',
        align: 'center',
      })
      .setOrigin(0.5);
    overlay.add(body);

    const btnY = cardY + cardH - 50;
    const cancel = this.add.container(w / 2 - 90, btnY);
    const cg = this.add.graphics();
    cg.fillStyle(0x374151, 1);
    cg.lineStyle(2, 0x0a0f1f, 1);
    cg.fillRoundedRect(-70, -22, 140, 44, 12);
    cg.strokeRoundedRect(-70, -22, 140, 44, 12);
    cancel.add(cg);
    cancel.add(
      this.add
        .text(0, 0, 'Cancel', {
          fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
          fontSize: '18px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    const ch = this.add.zone(0, 0, 150, 60).setInteractive({ useHandCursor: true });
    ch.on('pointerup', () => overlay.destroy(true));
    cancel.add(ch);
    overlay.add(cancel);

    const yes = this.add.container(w / 2 + 90, btnY);
    const yg = this.add.graphics();
    yg.fillStyle(0xef4444, 1);
    yg.lineStyle(2, 0x0a0f1f, 1);
    yg.fillRoundedRect(-70, -22, 140, 44, 12);
    yg.strokeRoundedRect(-70, -22, 140, 44, 12);
    yes.add(yg);
    yes.add(
      this.add
        .text(0, 0, 'Yes, Reset', {
          fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
          fontSize: '18px',
          color: '#0a0f1f',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    const yh = this.add.zone(0, 0, 150, 60).setInteractive({ useHandCursor: true });
    yh.on('pointerup', () => {
      resetSave();
      overlay.destroy(true);
      this.scene.restart();
    });
    yes.add(yh);
    overlay.add(yes);
  }
}
